import { demoConsole } from "resources/constants";
import { roiNumerator } from "lib/roi/strategies";

export const downloadBlob = (data, filename, fileExt) => {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement("a");
  const MAX_FILE_NAME_LENGTH = 255 - fileExt.length;
  const truncatedFilename = filename.substring(
    0,
    MAX_FILE_NAME_LENGTH - fileExt.length
  );

  link.href = url;
  link.setAttribute("download", `${truncatedFilename}.${fileExt}`);
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
};

export const nFormatter = (num) => {
  if (Math.abs(num) >= 1e3 && Math.abs(num) < 1e6)
    return `${(num / 1e3).toFixed(2)}K`;
  if (Math.abs(num) >= 1e6 && Math.abs(num) < 1e9)
    return `${(num / 1e6).toFixed(2)}M`;
  if (Math.abs(num) >= 1e9 && Math.abs(num) < 1e12)
    return `${(num / 1e9).toFixed(2)}B`;
  if (Math.abs(num) >= 1e12) return `${(num / 1e12).toFixed(2)}T`;
  if (num % 1 === 0) return Number(num).toString();
  return Number(num)
    .toFixed(2)
    .toString();
};

export const getNumberSign = (num) => {
  if (num > 0) return "+";
  if (num < 0) return "-";
  return "";
};

export const getCompanyNameFromUrl = (url) => {
  const tenantRegex = /console-(.*).cloud/;
  const result = url.match(tenantRegex);
  const tenantName = result ? result[1].replace("-staging", "") : undefined;

  return tenantName;
};

export const getTenantIdFromUrl = (url) => {
  const tenantRegex = /\/t\/(\d+)(\/.*)?/;
  const result = url.match(tenantRegex);

  return result ? result[1] : undefined;
};

export const getFormattedCellValue = (key, value) => {
  if (value === null) return "N/A";
  if (key === "price" || key === "price_in") {
    return value.toLocaleString("en", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  }

  return value.toLocaleString("en", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

export const normalizeBetweenTwoRanges = (
  val,
  minVal,
  maxVal,
  newMin,
  newMax
) => {
  return newMin + ((val - minVal) * (newMax - newMin)) / (maxVal - minVal);
};

export const getPercentageColor = (columnName, rowValue, referenceValue) => {
  const neutralColor = {
    backgroundColor: "#e2e2e2",
    color: "#4e4e4e",
  };
  const positiveColor = {
    backgroundColor: "rgba(0,128,94,0.1)",
    color: "#00805e",
  };

  const negativeColor = {
    backgroundColor: "rgba(228,86,123,0.1)",
    color: "#e4567b",
  };

  if (columnName === "price") return neutralColor;

  return (100 * (rowValue - referenceValue)) / referenceValue < 0
    ? negativeColor
    : positiveColor;
};

export const compareKeys = (a, b) => {
  const aKeys = Object.keys(a).sort();
  const bKeys = Object.keys(b).sort();
  return JSON.stringify(aKeys) === JSON.stringify(bKeys);
};

export const getFiltersKey = (filters) => {
  const filterKeys = {
    category:
      Array.from(filters?.category || [])
        .map((obj) => obj.id)
        .toString() || undefined,
    line:
      Array.from(filters.line || [])
        .map((obj) => obj.id)
        .toString() || undefined,
    brand:
      Array.from(filters.brand || [])
        .map((obj) => obj.id)
        .toString() || undefined,
    product:
      Array.from(filters.product || [])
        .map((obj) => obj.id)
        .toString() || undefined,
    retailer:
      Array.from(filters.retailer || [])
        .map((obj) => obj.id)
        .toString() || undefined,
    area:
      Array.from(filters.area || [])
        .map((obj) => obj.id)
        .toString() || undefined,
    retailerGroup:
      Array.from(filters.retailerGroup || [])
        .map((obj) => obj.id)
        .toString() || undefined,
  };

  return `${filterKeys.category}-${filterKeys.line}-${filterKeys.brand}-${filterKeys.product}-${filterKeys.retailer}-${filterKeys.area}-${filterKeys.retailerGroup}`;
};

export const getQueryFilters = (filters) => {
  return {
    ...(filters.category && {
      category_id: filters.category.map((filter) => filter.id),
    }),
    ...(filters.line && {
      line_id: filters.line.map((filter) => filter.id),
    }),
    ...(filters.brand && {
      brand_id: filters.brand.map((filter) => filter.id),
    }),
    ...(filters.product && {
      product_id: filters.product.map((filter) => filter.id),
    }),
    ...(filters.retailer && {
      retailer_id: filters.retailer.map((filter) => filter.id),
    }),
    ...(filters.area && {
      area_id: filters.area.map((filter) => filter.id),
    }),
    ...(filters.retailerGroup && {
      retailer_group_id: filters.retailerGroup.map((filter) => filter.id),
    }),
  };
};

export const matchQueryFilters = (obj, filters) => {
  // Function to iterate through every filter and evaluate if the triplet matches some of the items
  let foundMatch = true;
  Object.keys(filters).forEach((key) => {
    if (filters[key] && !filters[key].some((id) => obj[key] === id)) {
      foundMatch = false;
    }
  });
  return foundMatch;
};

export const isTenantUS = (tenant) => {
  let isUsTenant = false;
  if (demoConsole === tenant?.mysql_database) {
    isUsTenant = true;
  }
  return isUsTenant;
};

export const getROI = (
  newScenarioMargin,
  referenceScenarioMargin,
  investmentIn,
  { strategy = "standard", unitsEsc, unitsRef } = {}
) => {
  if (investmentIn === 0) return null;
  const numerator = roiNumerator(strategy, {
    marginEsc: newScenarioMargin,
    marginRef: referenceScenarioMargin,
    unitsEsc,
    unitsRef,
  });
  return `${((100 * numerator) / investmentIn).toFixed(2)} %`;
};

export const getNestedObject = (nestedObj, pathArr) => {
  return (
    pathArr.reduce((obj) =>
      obj && nestedObj[obj] !== "undefined" ? nestedObj : undefined
    ),
    nestedObj
  );
};
